import { MapProvider } from '../utils/mapUtils';
import {
  DEFAULT_WORKDAY_START_TIME,
  DEFAULT_WORKDAY_END_TIME,
  DEFAULT_BREAK_START_TIME,
  DEFAULT_BREAK_END_TIME,
} from '../config/workingTimeDefaults';

export interface UserPreferences {
  mapProvider: MapProvider;
  enableMapFallback: boolean;
  timezone: string;
  defaultVisitDuration: number;
  appointmentDurationOptions: number[];
  slotStepMinutes: number;
  agendaDenseMode: boolean;
  geocodingProviderOrder: string;
  workdayStartTime: string;
  workdayEndTime: string;
  enableMiddayBreak: boolean;
  breakStartTime: string;
  breakEndTime: string;
  professionalBaseAddress: string;
  professionalBaseLatitude: number | null;
  professionalBaseLongitude: number | null;
  returnToBaseForMiddayBreak: boolean;
}

const USER_PREFERENCES_STORAGE_KEY = 'travelopt.user-preferences.v1';
const USER_PREFERENCES_CHANGED_EVENT = 'travelopt.user-preferences.changed';
const DEFAULT_APPOINTMENT_DURATION_OPTIONS = [30, 60, 90, 120, 180, 240];
const MIN_APPOINTMENT_DURATION_MINUTES = 5;
const MAX_APPOINTMENT_DURATION_MINUTES = 480;

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  mapProvider: 'google',
  enableMapFallback: true,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris',
  defaultVisitDuration: 60,
  appointmentDurationOptions: DEFAULT_APPOINTMENT_DURATION_OPTIONS,
  slotStepMinutes: 30,
  agendaDenseMode: true,
  geocodingProviderOrder: 'geoplateforme,google,openrouteservice,nominatim',
  workdayStartTime: DEFAULT_WORKDAY_START_TIME,
  workdayEndTime: DEFAULT_WORKDAY_END_TIME,
  enableMiddayBreak: true,
  breakStartTime: DEFAULT_BREAK_START_TIME,
  breakEndTime: DEFAULT_BREAK_END_TIME,
  returnToBaseForMiddayBreak: true,
  professionalBaseAddress: '',
  professionalBaseLatitude: null,
  professionalBaseLongitude: null,
};

const isBrowser = (): boolean => {
  return globalThis.window !== undefined && globalThis.localStorage !== undefined;
};

const sanitizeMapProvider = (value: unknown): MapProvider => {
  return value === 'osm' ? 'osm' : 'google';
};

const sanitizeTimezone = (value: unknown): string => {
  if (typeof value !== 'string') {
    return DEFAULT_USER_PREFERENCES.timezone;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : DEFAULT_USER_PREFERENCES.timezone;
};

const sanitizeDefaultVisitDuration = (value: unknown): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return DEFAULT_USER_PREFERENCES.defaultVisitDuration;
  }

  return Math.min(240, Math.max(15, Math.round(numeric)));
};

const normalizeAppointmentDuration = (value: unknown): number | null => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  const rounded = Math.round(numeric);
  if (
    rounded < MIN_APPOINTMENT_DURATION_MINUTES ||
    rounded > MAX_APPOINTMENT_DURATION_MINUTES
  ) {
    return null;
  }

  return rounded;
};

const sanitizeAppointmentDurationOptions = (value: unknown): number[] => {
  let rawValues: unknown[] = [];

  if (Array.isArray(value)) {
    rawValues = value;
  } else if (typeof value === 'string') {
    rawValues = value
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  const normalized = rawValues
    .map((item) => normalizeAppointmentDuration(item))
    .filter((item): item is number => item !== null);

  const uniqueSorted = Array.from(new Set(normalized)).sort((left, right) => left - right);

  if (uniqueSorted.length === 0) {
    return DEFAULT_USER_PREFERENCES.appointmentDurationOptions;
  }

  return uniqueSorted;
};

const VALID_SLOT_STEP_MINUTES = [5, 10, 15, 30, 60] as const;

const sanitizeSlotStepMinutes = (value: unknown): number => {
  const numeric = Math.round(Number(value));
  if ((VALID_SLOT_STEP_MINUTES as readonly number[]).includes(numeric)) {
    return numeric;
  }
  return DEFAULT_USER_PREFERENCES.slotStepMinutes;
};

const sanitizeGeocodingProviderOrder = (value: unknown): string => {
  if (typeof value !== 'string') {
    return DEFAULT_USER_PREFERENCES.geocodingProviderOrder;
  }

  const normalized = value
    .split(',')
    .map((provider) => provider.trim())
    .filter((provider) => provider.length > 0)
    .join(',');

  return normalized || DEFAULT_USER_PREFERENCES.geocodingProviderOrder;
};

const isValidTimeString = (value: string): boolean => {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
};

const sanitizeTimeString = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value.trim();
  return isValidTimeString(normalized) ? normalized : fallback;
};

const toMinutesFromTime = (value: string): number => {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const sanitizeProfessionalBaseAddress = (value: unknown): string => {
  if (typeof value !== 'string') {
    return DEFAULT_USER_PREFERENCES.professionalBaseAddress;
  }

  return value.trim();
};

const sanitizeBoundedCoordinate = (
  value: unknown,
  min: number,
  max: number,
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  if (numeric < min || numeric > max) {
    return null;
  }

  return Math.round(numeric * 1_000_000) / 1_000_000;
};

const sanitizeUserPreferences = (value: unknown): UserPreferences => {
  const unsafe = (value && typeof value === 'object') ? (value as Partial<UserPreferences>) : {};

  const workdayStartTime = sanitizeTimeString(
    unsafe.workdayStartTime,
    DEFAULT_USER_PREFERENCES.workdayStartTime,
  );
  const rawWorkdayEndTime = sanitizeTimeString(
    unsafe.workdayEndTime,
    DEFAULT_USER_PREFERENCES.workdayEndTime,
  );
  const workdayEndTime =
    toMinutesFromTime(rawWorkdayEndTime) > toMinutesFromTime(workdayStartTime)
      ? rawWorkdayEndTime
      : DEFAULT_USER_PREFERENCES.workdayEndTime;

  const enableMiddayBreak = Boolean(
    unsafe.enableMiddayBreak ?? DEFAULT_USER_PREFERENCES.enableMiddayBreak,
  );
  const returnToBaseForMiddayBreak = Boolean(
    unsafe.returnToBaseForMiddayBreak ?? DEFAULT_USER_PREFERENCES.returnToBaseForMiddayBreak,
  );
  const rawBreakStartTime = sanitizeTimeString(
    unsafe.breakStartTime,
    DEFAULT_USER_PREFERENCES.breakStartTime,
  );
  const rawBreakEndTime = sanitizeTimeString(
    unsafe.breakEndTime,
    DEFAULT_USER_PREFERENCES.breakEndTime,
  );

  const breakStartMinutes = toMinutesFromTime(rawBreakStartTime);
  const breakEndMinutes = toMinutesFromTime(rawBreakEndTime);
  const workdayStartMinutes = toMinutesFromTime(workdayStartTime);
  const workdayEndMinutes = toMinutesFromTime(workdayEndTime);

  const hasValidBreakWindow =
    breakStartMinutes >= workdayStartMinutes &&
    breakEndMinutes <= workdayEndMinutes &&
    breakEndMinutes > breakStartMinutes;

  const breakStartTime = hasValidBreakWindow
    ? rawBreakStartTime
    : DEFAULT_USER_PREFERENCES.breakStartTime;
  const breakEndTime = hasValidBreakWindow
    ? rawBreakEndTime
    : DEFAULT_USER_PREFERENCES.breakEndTime;

  return {
    mapProvider: sanitizeMapProvider(unsafe.mapProvider),
    enableMapFallback: Boolean(unsafe.enableMapFallback ?? DEFAULT_USER_PREFERENCES.enableMapFallback),
    timezone: sanitizeTimezone(unsafe.timezone),
    defaultVisitDuration: sanitizeDefaultVisitDuration(unsafe.defaultVisitDuration),
    appointmentDurationOptions: sanitizeAppointmentDurationOptions(unsafe.appointmentDurationOptions),
    slotStepMinutes: sanitizeSlotStepMinutes(unsafe.slotStepMinutes),
    agendaDenseMode: Boolean(unsafe.agendaDenseMode ?? DEFAULT_USER_PREFERENCES.agendaDenseMode),
    geocodingProviderOrder: sanitizeGeocodingProviderOrder(unsafe.geocodingProviderOrder),
    workdayStartTime,
    workdayEndTime,
    enableMiddayBreak,
    breakStartTime,
    breakEndTime,
    professionalBaseAddress: sanitizeProfessionalBaseAddress(unsafe.professionalBaseAddress),
    professionalBaseLatitude: sanitizeBoundedCoordinate(unsafe.professionalBaseLatitude, -90, 90),
    professionalBaseLongitude: sanitizeBoundedCoordinate(unsafe.professionalBaseLongitude, -180, 180),
    returnToBaseForMiddayBreak,
  };
};

const emitUserPreferencesChanged = (preferences: UserPreferences): void => {
  if (!isBrowser()) {
    return;
  }

  globalThis.window.dispatchEvent(
    new CustomEvent<UserPreferences>(USER_PREFERENCES_CHANGED_EVENT, {
      detail: preferences,
    }),
  );
};

export const getUserPreferences = (): UserPreferences => {
  if (!isBrowser()) {
    return { ...DEFAULT_USER_PREFERENCES };
  }

  const raw = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY);
  if (!raw) {
    return { ...DEFAULT_USER_PREFERENCES };
  }

  try {
    const parsed = JSON.parse(raw);
    return sanitizeUserPreferences(parsed);
  } catch {
    return { ...DEFAULT_USER_PREFERENCES };
  }
};

export const saveUserPreferences = (preferences: UserPreferences): UserPreferences => {
  const sanitized = sanitizeUserPreferences(preferences);

  if (isBrowser()) {
    localStorage.setItem(USER_PREFERENCES_STORAGE_KEY, JSON.stringify(sanitized));
  }

  emitUserPreferencesChanged(sanitized);

  return sanitized;
};

export const updateUserPreferences = (partialPreferences: Partial<UserPreferences>): UserPreferences => {
  const current = getUserPreferences();
  const merged = {
    ...current,
    ...partialPreferences,
  };

  return saveUserPreferences(merged);
};

export const resetUserPreferences = (): UserPreferences => {
  if (isBrowser()) {
    localStorage.removeItem(USER_PREFERENCES_STORAGE_KEY);
  }

  const resetPreferences = { ...DEFAULT_USER_PREFERENCES };
  emitUserPreferencesChanged(resetPreferences);
  return resetPreferences;
};

export const subscribeUserPreferences = (
  onChange: (preferences: UserPreferences) => void,
): (() => void) => {
  if (!isBrowser()) {
    return () => {
      // No-op in non-browser contexts.
    };
  }

  const handlePreferencesEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<UserPreferences>;
    const nextPreferences = sanitizeUserPreferences(customEvent.detail);
    onChange(nextPreferences);
  };

  const handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== USER_PREFERENCES_STORAGE_KEY) {
      return;
    }

    onChange(getUserPreferences());
  };

  globalThis.window.addEventListener(USER_PREFERENCES_CHANGED_EVENT, handlePreferencesEvent as EventListener);
  globalThis.window.addEventListener('storage', handleStorageEvent);

  return () => {
    globalThis.window.removeEventListener(USER_PREFERENCES_CHANGED_EVENT, handlePreferencesEvent as EventListener);
    globalThis.window.removeEventListener('storage', handleStorageEvent);
  };
};

export const DEFAULT_GEO_PROVIDER_ORDER = DEFAULT_USER_PREFERENCES.geocodingProviderOrder;
